<template>
  <SummarizationContainerCommon
    :loading="loading"
    :is-transcription-result-empty="isTranscriptionResultEmpty"
  >
    <SummarizationHeaderCommon
      :created-at="timeline?.createdAt"
      :is-refetch-button-visible="!timeline"
      :can-recreate="canRecreate"
      @click:recreate="handleRecreateClick"
      @click:refetch="fetchTimeline"
    />
    <template v-if="!!timeline">
      <div class="timeline-annotation">
        {{ i18n.t('transcription.timelineAnnotation') }}
      </div>
      <SummarizationContentCommon
        :content="timeline?.timelineMarkdown"
        @click:play-at="handlePlayAtClick"
      />
    </template>
    <template v-else>
      <SummarizationErrorBodyCommon
        :resource-name="i18n.t('transcription.timeline')"
      />
    </template>
  </SummarizationContainerCommon>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { TranscriptionTimeline } from '@/api/openapi';
import { TranscriptionTimelineApiService } from '@/api/user/resources/transcription_timeline';
import { useAlert } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import SummarizationContainerCommon from './SummarizationContainerCommon.vue';
import SummarizationContentCommon from './SummarizationContentCommon.vue';
import SummarizationErrorBodyCommon from './SummarizationErrorBodyCommon.vue';
import SummarizationHeaderCommon from './SummarizationHeaderCommon.vue';

type TProps = {
  zoomPhoneResultId: number | null;
  isTranscriptionResultEmpty: boolean;
};
type TEmits = {
  'click:playAt': [time: number];
}
const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

const timeline = ref<TranscriptionTimeline | null>(null);

const { doActionWithWait, wait } = useWait();
const i18n = useI18n();
const { alert } = useAlert();

const loading = computed(() => {
  return wait.is(['fetchTimelineWait', 'postTranscriptionSummarizeInTimelineWait']);
});

const canRecreate = computed(() => {
  return !timeline.value;
});

const fetchTimeline = async () => {
  if (props.isTranscriptionResultEmpty) return;
  doActionWithWait('fetchTimelineWait', async () => {
    const api = new TranscriptionTimelineApiService();
    const { data: { transcriptionTimeline } } = await api.getTranscriptionTimeline({
      request: {
        zoomPhoneResultId: props.zoomPhoneResultId,
      },
      errorHandlers: {
        404: () => {
          timeline.value = null;
        },
      },
    });
    timeline.value = transcriptionTimeline;
  });
};

const postTranscriptionSummarizeInTimelineErrorHandler = () => {
  alert({
    title: i18n.t('transcription.failedToRecreate.title', { resourceName: i18n.t('transcription.timeline') }),
    message: i18n.t('transcription.failedToRecreate.message', { resourceName: i18n.t('transcription.timeline') }),
  });
};

const postTranscriptionSummarizeInTimeline = async () => {
  if (!props.zoomPhoneResultId) return;
  doActionWithWait('postTranscriptionSummarizeInTimelineWait', async () => {
    const api = new TranscriptionTimelineApiService();
    await api.postTranscriptionSummarizeInTimeline({
      request: { zoomPhoneResultId: props.zoomPhoneResultId },
      errorHandlers: {
        500: postTranscriptionSummarizeInTimelineErrorHandler,
        422: postTranscriptionSummarizeInTimelineErrorHandler,
      },
    });
    await fetchTimeline();
  });
};

const handleRecreateClick = async () => {
  postTranscriptionSummarizeInTimeline();
};

const handlePlayAtClick = (time: number) => {
  emit('click:playAt', time);
};

onBeforeMount(async () => {
  await fetchTimeline();
});
</script>

<style lang="scss" scoped>
.timeline-annotation {
  color: $textcolor-light;
  font-size: $fontsize-100;
  margin-bottom: 10px;
}
</style>
